import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { STORAGE_KEYS } from "../lib/constants";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    loading: true,
    user: null,
    company: null,
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      setState({ loading: false, user: null, company: null });
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setState({ loading: false, user: data.user, company: data.company });
      return data;
    } catch {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      setState({ loading: false, user: null, company: null });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
      setState({ loading: false, user: data.user, company: data.company });
      return { ok: true, user: data.user, company: data.company };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const signup = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
      setState({ loading: false, user: data.user, company: data.company });
      return { ok: true, user: data.user, company: data.company, trial_ends_at: data.trial_ends_at };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    setState({ loading: false, user: null, company: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
